import { z } from 'zod/v4';

import { ActionPermission } from '../../backend-generated-types';
import { endpoint } from '../http/endpoint';
import {
    UserDTORuntype,
    UserInviteDTORuntype,
    UserInviteInsertDTORuntype,
    UserInviteInternalRoleUpdateDTORuntype,
    UserRolesDTORuntype,
    UserRolesUpdateDTORuntype,
    UserUpdateDTORuntype,
} from './userBackendTypes';

export const userEndpoints = {
    'GET /users/:userId': endpoint({
        description: 'Fetches a single user by ID',
        pathParams: z.object({ userId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: UserDTORuntype }),
    }),

    'DELETE /users/:userId': endpoint({
        description: 'Deletes a single user by ID',
        pathParams: z.object({ userId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        actionPermission: ({ targetUserType }: { targetUserType: 'internal' | 'customer' }): ActionPermission => {
            return targetUserType === 'internal' ? 'CanDeleteInternalUser' : 'CanDeleteCustomerUser';
        },
        invalidates: ['GET /users/:userId', 'GET /users/organization'],
    }),

    'PATCH /users/:userId': endpoint({
        description: 'Updates a single user by ID',
        pathParams: z.object({ userId: z.string() }),
        queryParams: z.undefined(),
        requestBody: UserUpdateDTORuntype,
        responseBody: z.object({ data: UserDTORuntype }),
        invalidates: ['GET /users/:userId', 'GET /users/organization'],
        actionPermission: ({ targetUserType }: { targetUserType: 'internal' | 'customer' }): ActionPermission => {
            return targetUserType === 'internal' ? 'CanUpdateInternalUser' : 'CanUpdateCustomerUser';
        },
    }),

    'GET /users/:userId/roles': endpoint({
        description: 'Fetches roles for a single user by ID',
        pathParams: z.object({ userId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: UserRolesDTORuntype }),
    }),

    'PATCH /users/:userId/roles': endpoint({
        description: 'Updates roles for a single user',
        pathParams: z.object({ userId: z.string() }),
        queryParams: z.undefined(),
        requestBody: UserRolesUpdateDTORuntype,
        responseBody: z.object({ data: UserRolesDTORuntype }),
        invalidates: [
            'POST /users/roles/bulk',
            'GET /users/:userId',
            'GET /users/:userId/roles',
            'GET /users/organization',
        ],
        actionPermission: ({ targetUserType }: { targetUserType: 'internal' | 'customer' }): ActionPermission => {
            return targetUserType === 'internal' ? 'CanUpdateInternalUser' : 'CanUpdateCustomerUser';
        },
    }),

    'POST /users/roles/bulk': endpoint({
        description: 'Fetches a list of user roles given their IDs',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ ids: z.array(z.string()) }),
        responseBody: z.object({ data: z.array(UserRolesDTORuntype) }),
    }),

    'GET /users/organization': endpoint({
        description: 'Fetches all the EMS users in the given organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(UserDTORuntype) }),
        actionPermission: () => 'CanViewOrganizationUsers',
    }),

    'GET /users/organization/invites': endpoint({
        description: 'Gets all organization user invites',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(UserInviteDTORuntype) }),
        actionPermission: () => 'CanViewInternalUserInvite',
    }),

    'POST /users/organization/invites': endpoint({
        description: 'Sends many organization user invites',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.array(UserInviteInsertDTORuntype),
        responseBody: z.unknown(),
        invalidates: ['GET /users/organization/invites'],
        actionPermission: () => 'CanInviteInternalUsers',
    }),

    'POST /users/organization/support-user': endpoint({
        description: 'Adds the support user to the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        invalidates: ['GET /users/organization'],
        actionPermission: () => 'CanAddSupportUser',
    }),

    'DELETE /users/organization/support-user': endpoint({
        description: 'Removes the support user to the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        invalidates: ['GET /users/organization'],
        actionPermission: () => 'CanRemoveSupportUser',
    }),

    'GET /users/customers/:customerId': endpoint({
        description: 'Fetches all the users of the given customer',
        pathParams: z.object({ customerId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(UserDTORuntype) }),
    }),

    'GET /users/customers/:customerId/invites': endpoint({
        description: 'Gets all user invites for a customer',
        pathParams: z.object({ customerId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(UserInviteDTORuntype) }),
        actionPermission: () => 'CanViewCustomerUserInvite',
    }),

    'POST /users/customers/:customerId/invites': endpoint({
        description: 'Sends many customer user invites',
        pathParams: z.object({ customerId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.array(UserInviteInsertDTORuntype),
        responseBody: z.unknown(),
        invalidates: ['GET /users/customers/:customerId/invites'],
        actionPermission: () => 'CanInviteCustomerUsers',
    }),

    'DELETE /users/invites/:inviteId': endpoint({
        description: 'Deletes a single user invitation by id',
        pathParams: z.object({ inviteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        invalidates: ['GET /users/organization/invites'],
        actionPermission: () => 'CanDeleteInternalUserInvite',
    }),

    'PATCH /users/invites/:inviteId/internal-role': endpoint({
        description: 'Updates the internal role for an invite',
        pathParams: z.object({ inviteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: UserInviteInternalRoleUpdateDTORuntype,
        responseBody: z.unknown(),
        invalidates: ['GET /users/organization/invites'],
        actionPermission: () => 'CanUpdateInternalUserRole',
    }),
};
